import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import toast from 'react-hot-toast';
import { cldImg } from '../../utils/cloudinary';

function WishlistCard({ item, onRemove }) {
  const { addToCart } = useCart();
  const product = item.product;
  if (!product) return null;

  const image = product.images?.[0]?.image_url;
  const variant = product.variants?.[0];
  const price = variant?.price || 0;
  const comparePrice = variant?.compare_price || 0;
  const discount = comparePrice > price ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;

  const handleAddToCart = () => {
    addToCart(product, variant);
    toast.success('Added to cart!');
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden group card-hover">
      {/* Image */}
      <Link to={`/product/${product.slug}`} className="block relative overflow-hidden aspect-square bg-gray-50">
        {image ? (
          <img src={cldImg(image, 400)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">🛍️</div>
        )}
        {discount > 0 && (
          <span className="absolute top-2 left-2 badge-sale text-xs px-2 py-0.5 rounded-lg font-bold">{discount}% OFF</span>
        )}
        <button
          onClick={() => onRemove(item.id, product.id)}
          className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-50 hover:text-brand-red transition-colors"
        >
          <Trash2 size={14} className="text-gray-400 hover:text-brand-red" />
        </button>
      </Link>

      <div className="p-3">
        <p className="text-xs text-gray-400 mb-1">{product.category?.name}</p>
        <Link to={`/product/${product.slug}`} className="font-semibold text-brand-dark text-sm line-clamp-2 hover:text-brand-yellow transition-colors">
          {product.name}
        </Link>

        {/* Rating placeholder */}
        <div className="flex items-center gap-1 mt-1.5">
          {[1,2,3,4,5].map(s => <Star key={s} size={10} className={s <= 4 ? 'fill-brand-yellow text-brand-yellow' : 'text-gray-200'} />)}
          <span className="text-xs text-gray-400 ml-1">(24)</span>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <span className="font-bold text-brand-dark">₹{price.toLocaleString('en-IN')}</span>
          {comparePrice > price && (
            <span className="text-xs text-gray-400 line-through">₹{comparePrice.toLocaleString('en-IN')}</span>
          )}
        </div>

        <button
          onClick={handleAddToCart}
          disabled={!variant}
          className="mt-3 w-full btn-primary py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <ShoppingBag size={15} />
          Add to Cart
        </button>
      </div>
    </div>
  );
}

export default function WishlistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { wishlistItems: items, loading, toggleWishlist } = useWishlist();

  useEffect(() => {
    if (!user) navigate('/login', { state: { from: { pathname: '/wishlist' } } });
  }, [user]);

  const handleRemove = (wishlistId, productId) => toggleWishlist(productId);

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-brand-red/10 rounded-xl flex items-center justify-center">
          <Heart size={20} className="text-brand-red fill-brand-red" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-brand-dark">My Wishlist</h1>
          <p className="text-sm text-gray-400">{items.length} {items.length === 1 ? 'item' : 'items'} saved</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
              <div className="aspect-square bg-gray-100" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-2/3" />
                <div className="h-4 bg-gray-100 rounded" />
                <div className="h-4 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <Heart size={36} className="text-brand-red/30" />
          </div>
          <h3 className="text-lg font-bold text-brand-dark mb-2">Your wishlist is empty</h3>
          <p className="text-gray-400 text-sm mb-8 max-w-xs mx-auto">Save items you love and come back to them anytime</p>
          <Link to="/products" className="btn-primary px-8 py-3 rounded-xl font-semibold">
            Explore Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map(item => (
            <WishlistCard key={item.id} item={item} onRemove={handleRemove} />
          ))}
        </div>
      )}
    </div>
  );
}
